import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { GeschaeftspartnerLogo } from "@/components/GeschaeftspartnerLogo";
import type { Geschaeftspartner, GeschaeftspartnerTyp } from "@/lib/kontakte";
import { EhDataTableShell, SortableHeader, type SortState } from "@/components/dataTable";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";
import {
  GESCHAEFTSPARTNER_SORTABLE_COLUMNS,
  clampGeschaeftspartnerColumnWidth,
  formatGeschaeftspartnerAktivitaet,
  getGeschaeftspartnerColumnDefaultWidth,
  getGeschaeftspartnerColumnLabel,
  renderGeschaeftspartnerCellValue,
  type GeschaeftspartnerColumnKey,
} from "@/pages/kontakte/geschaeftspartnerColumns";
import { PrivatpersonBadge } from "@/pages/kontakte/PartnerNameFields";

export interface GeschaeftspartnerTableProps {
  rows: Geschaeftspartner[];
  columns: GeschaeftspartnerColumnKey[];
  widths: Record<string, number>;
  onColumnWidthChange?: (key: string, width: number) => void;
  onRowClick?: (row: Geschaeftspartner) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
  emptyText?: string;
  /**
   * „Top zuerst“-Modus: unter dem Firmennamen die Aktivität
   * (Projekte bzw. Bestellungen/E-Rechnungen).
   */
  ranking?: { typ: GeschaeftspartnerTyp } | null;
}

function TableHead({
  columns,
  activeResize,
  onResizePointerDown,
  sort,
  onSort,
}: {
  columns: GeschaeftspartnerColumnKey[];
  activeResize: string | null;
  onResizePointerDown: (e: React.PointerEvent, colKey: string, idx: number) => void;
  sort?: SortState | null;
  onSort?: (key: string) => void;
}) {
  return (
    <thead className="eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary">
      <tr>
        {columns.map((col, idx) => (
          <th
            key={col}
            scope="col"
            className="eh-data-th whitespace-nowrap px-3 py-2 text-left font-semibold leading-snug"
          >
            {GESCHAEFTSPARTNER_SORTABLE_COLUMNS.has(col) && onSort ? (
              <SortableHeader
                label={getGeschaeftspartnerColumnLabel(col)}
                columnKey={col}
                sort={sort ?? null}
                onSort={onSort}
              />
            ) : (
              <span className="block truncate" title={getGeschaeftspartnerColumnLabel(col)}>
                {getGeschaeftspartnerColumnLabel(col)}
              </span>
            )}
            <span
              className={clsx("eh-data-resizer", activeResize === col && "is-active")}
              role="separator"
              aria-orientation="vertical"
              title="Spaltenbreite ziehen"
              onPointerDown={(e) => onResizePointerDown(e, col, idx)}
            />
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function GeschaeftspartnerTable({
  rows,
  columns,
  widths,
  onColumnWidthChange,
  onRowClick,
  sort,
  onSort,
  emptyText = "Keine Einträge gefunden.",
  ranking = null,
}: GeschaeftspartnerTableProps) {
  const dataWidths = useMemo(
    () =>
      columns.map((col) => {
        const stored = Number(widths?.[col]);
        return Number.isFinite(stored) && stored > 0
          ? clampGeschaeftspartnerColumnWidth(stored)
          : getGeschaeftspartnerColumnDefaultWidth(col);
      }),
    [columns, widths],
  );

  const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const floatingColRefs = useRef<Record<string, HTMLTableColElement | null>>({});
  const [activeResize, setActiveResize] = useState<string | null>(null);
  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  const floatingSyncKey = useMemo(
    () => [columns.join("|"), dataWidths.join(","), rows.length].join("§"),
    [columns, dataWidths, rows.length],
  );

  const floating = useFloatingProjectTableHeader(tableWrapRef, tableRef, {
    enabled: columns.length > 0,
    syncKey: floatingSyncKey,
  });

  function startResize(event: React.PointerEvent, colKey: string, idx: number) {
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.setPointerCapture(event.pointerId);
    setActiveResize(colKey);

    const startX = event.clientX;
    const startWidth = dataWidths[idx] ?? getGeschaeftspartnerColumnDefaultWidth(colKey);
    const colEl = colRefs.current[colKey];
    let lastWidth = startWidth;

    function applyWidth(width: number) {
      lastWidth = clampGeschaeftspartnerColumnWidth(width);
      if (colEl) colEl.style.width = `${lastWidth}px`;
      const floatCol = floatingColRefs.current[colKey];
      if (floatCol) floatCol.style.width = `${lastWidth}px`;
    }

    function onMove(e: PointerEvent) {
      applyWidth(startWidth + (e.clientX - startX));
    }

    function onUp() {
      target.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setActiveResize(null);
      if (lastWidth !== startWidth) onColumnWidthChange?.(colKey, lastWidth);
    }

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  return (
    <>
      <EhDataTableShell ref={tableWrapRef}>
        <table ref={tableRef} className="eh-data-table divide-y divide-separator text-sm">
          <colgroup>
            {columns.map((col, idx) => (
              <col
                key={col}
                ref={(el) => {
                  colRefs.current[col] = el;
                }}
                style={{ width: `${dataWidths[idx]}px` }}
              />
            ))}
          </colgroup>

          <TableHead
            columns={columns}
            activeResize={activeResize}
            onResizePointerDown={startResize}
            sort={sort}
            onSort={onSort}
          />

          <tbody className="divide-y divide-separator bg-card">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-8 text-center text-sm text-label-tertiary"
                >
                  {emptyText}
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr
                key={row.id}
                className={clsx(
                  "eh-data-row align-middle transition hover:bg-fill-tertiary/55",
                  onRowClick && "cursor-pointer",
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => {
                  const cell = renderGeschaeftspartnerCellValue(row, col);
                  return (
                    <td
                      key={col}
                      className={clsx(
                        "eh-data-body-cell min-w-0 truncate px-3 py-2.5 align-middle",
                        cell.mono && "tabular-nums",
                        cell.alignRight && "text-right",
                        col === "firmenname" ? "text-label" : "text-label-secondary",
                      )}
                      title={cell.value !== "—" ? cell.value : undefined}
                    >
                      {col === "firmenname" ? (
                        <div className="flex min-w-0 items-center gap-3">
                          <GeschaeftspartnerLogo
                            partnerId={row.id}
                            name={row.firmenname}
                            hasLogo={row.has_logo}
                            logoUpdatedAt={row.logo_updated_at}
                            className="h-9 w-9 rounded-xl text-xs"
                          />
                          <span className="min-w-0">
                            <span className="flex min-w-0 items-center gap-2">
                              <span className="truncate font-medium text-label">{row.firmenname}</span>
                              {row.partnerart === "privatperson" ? <PrivatpersonBadge /> : null}
                            </span>
                            {row.matched_ansprechpartner ? (
                              <span className="block truncate text-xs text-label-secondary">
                                Ansprechpartner: {row.matched_ansprechpartner}
                              </span>
                            ) : null}
                            {ranking ? (
                              <span className="block truncate text-xs text-label-secondary">
                                {formatGeschaeftspartnerAktivitaet(row, ranking.typ)}
                              </span>
                            ) : null}
                          </span>
                        </div>
                      ) : (
                        cell.value
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </EhDataTableShell>

      {floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{ top: floating.top, left: floating.left, width: floating.width }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <colgroup>
                  {columns.map((col, idx) => (
                    <col
                      key={col}
                      ref={(el) => {
                        floatingColRefs.current[col] = el;
                      }}
                      style={{ width: `${dataWidths[idx]}px` }}
                    />
                  ))}
                </colgroup>
                <TableHead
                  columns={columns}
                  activeResize={activeResize}
                  onResizePointerDown={startResize}
                  sort={sort}
                  onSort={onSort}
                />
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
